import { useCallback, useEffect, useRef, useState } from 'react'
import type { DraftPatchPayload } from '../../../../../../../services/projectAutofillDraftService.ts'
import {
  getProjectAutofillDraft,
  patchProjectAutofillDraft
} from '../../../../../../../services/projectAutofillDraftService.ts'
import type {
  CommittedDraftSnapshot,
  RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  extractDraftSubprojectsFromApiResponse,
  mergeRubroSheetStatesFromDraftSubprojects,
  rubroHasValidSelectedSkill,
  serializeRubrosSnapshot,
  validationBundleFromPatchResponse
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { rubroHasGeneratedBudgetSheet } from '../../../../../../../utils/conversationalMode/rubroMetricBudgetCoherenceUtils.ts'
import { findRubroRowIndex } from '../projectAutofillCardUtils.ts'
import {
  BUDGET_SHEET_REQUIRES_METRIC_COMPUTATION_CODE,
  metricSheetHasUsableRows,
  parseProjectAutofillApiError,
  resolveMetricComputationView,
  type ReviewStage
} from '../../../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'

export type MetricBudgetSyncPhase = 'idle' | 'pending' | 'syncing' | 'error'

const METRIC_BUDGET_SYNC_DEBOUNCE_MS = 1000
const METRIC_BUDGET_SYNC_AFTER_IDLE_MS = 400

export type UseRubroMetricBudgetSilentSyncArgs = {
  draftId: string | undefined
  draftLocked: boolean
  isSaving: boolean
  fuzzyNeedsConfirm: boolean
  reviewStage: ReviewStage | null
  rubros: RubroRow[]
  buildSubprojectsPayload: (
    rows: RubroRow[],
    options?: { omitBudgetSheetTempIds?: ReadonlySet<string> }
  ) => NonNullable<DraftPatchPayload['subprojects']>
  patchMetadataPayload: () => NonNullable<DraftPatchPayload['metadata']>
  applyPatchResponseToValidation: (res: unknown, id: string) => Promise<void>
  committedRef: React.MutableRefObject<CommittedDraftSnapshot | null>
  setRubros: React.Dispatch<React.SetStateAction<RubroRow[]>>
  onBudgetSheetMetricError?: (tempId: string) => void
}

export function useRubroMetricBudgetSilentSync(args: UseRubroMetricBudgetSilentSyncArgs) {
  const {
    draftId,
    draftLocked,
    isSaving,
    fuzzyNeedsConfirm,
    reviewStage,
    rubros,
    buildSubprojectsPayload,
    patchMetadataPayload,
    applyPatchResponseToValidation,
    committedRef,
    setRubros,
    onBudgetSheetMetricError
  } = args

  const rubrosRef = useRef(rubros)
  rubrosRef.current = rubros

  const debounceTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const syncInFlightRef = useRef(false)
  const queuedTempIdRef = useRef<string | null>(null)

  const [syncPhaseByTempId, setSyncPhaseByTempId] = useState<Record<string, MetricBudgetSyncPhase>>({})

  const setPhase = useCallback((tempId: string, phase: MetricBudgetSyncPhase) => {
    setSyncPhaseByTempId((prev) => {
      if (prev[tempId] === phase) return prev
      return { ...prev, [tempId]: phase }
    })
  }, [])

  const canSyncRubro = useCallback(
    (row: RubroRow | undefined): row is RubroRow => {
      if (!row) return false
      if (!draftId || draftLocked || isSaving || fuzzyNeedsConfirm) return false
      if (reviewStage !== 'quotation_review') return false
      if (!rubroHasValidSelectedSkill(row)) return false
      if (!rubroHasGeneratedBudgetSheet(row)) return false
      if (resolveMetricComputationView(row) !== 'editable_grid') return false
      if (!metricSheetHasUsableRows(row.metricSheetState)) return false
      return true
    },
    [draftId, draftLocked, isSaving, fuzzyNeedsConfirm, reviewStage]
  )

  const runSync = useCallback(
    async (tempId: string) => {
      const rowIdx = findRubroRowIndex(rubrosRef.current, tempId)
      const row = rowIdx >= 0 ? rubrosRef.current[rowIdx] : undefined
      if (!canSyncRubro(row)) {
        setPhase(tempId, 'idle')
        return
      }
      syncInFlightRef.current = true
      setPhase(tempId, 'syncing')
      try {
        const omit = new Set([tempId])
        const patchBody = {
          subprojects: buildSubprojectsPayload(rubrosRef.current, { omitBudgetSheetTempIds: omit }),
          metadata: patchMetadataPayload()
        }
        const res = await patchProjectAutofillDraft(String(draftId), patchBody)
        await applyPatchResponseToValidation(res, String(draftId))

        let subs = extractDraftSubprojectsFromApiResponse(res)
        if (!subs?.length) {
          const fresh = await getProjectAutofillDraft(String(draftId))
          subs = extractDraftSubprojectsFromApiResponse(fresh) ?? undefined
        }

        const mergeIds = new Set([tempId])
        setRubros((prev) => {
          const merged = mergeRubroSheetStatesFromDraftSubprojects(prev, subs, mergeIds)
          const c = committedRef.current
          if (c) {
            committedRef.current = {
              ...c,
              rubrosKey: serializeRubrosSnapshot(merged),
              rubrosCount: merged.length
            }
          }
          return merged
        })
        setPhase(tempId, 'idle')
      } catch (e) {
        const parsed = parseProjectAutofillApiError(e)
        if (
          parsed.code === BUDGET_SHEET_REQUIRES_METRIC_COMPUTATION_CODE &&
          parsed.tempId &&
          onBudgetSheetMetricError
        ) {
          onBudgetSheetMetricError(parsed.tempId)
        }
        setPhase(tempId, 'error')
      } finally {
        syncInFlightRef.current = false
        const queued = queuedTempIdRef.current
        queuedTempIdRef.current = null
        if (queued && queued !== tempId) {
          void runSync(queued)
        }
      }
    },
    [
      draftId,
      canSyncRubro,
      buildSubprojectsPayload,
      patchMetadataPayload,
      applyPatchResponseToValidation,
      committedRef,
      setRubros,
      onBudgetSheetMetricError,
      setPhase
    ]
  )

  const scheduleMetricBudgetSync = useCallback(
    (tempId: string, opts?: { afterMetricIdleCommit?: boolean }) => {
      const rowIdx = findRubroRowIndex(rubrosRef.current, tempId)
      const row = rowIdx >= 0 ? rubrosRef.current[rowIdx] : undefined
      if (!canSyncRubro(row)) return

      const prevTimer = debounceTimersRef.current[tempId]
      if (prevTimer) clearTimeout(prevTimer)

      const delay = opts?.afterMetricIdleCommit
        ? METRIC_BUDGET_SYNC_AFTER_IDLE_MS
        : METRIC_BUDGET_SYNC_DEBOUNCE_MS

      setPhase(tempId, 'pending')
      debounceTimersRef.current[tempId] = setTimeout(() => {
        delete debounceTimersRef.current[tempId]
        if (syncInFlightRef.current) {
          queuedTempIdRef.current = tempId
          return
        }
        void runSync(tempId)
      }, delay)
    },
    [canSyncRubro, runSync, setPhase]
  )

  const cancelMetricBudgetSync = useCallback((tempId: string) => {
    const t = debounceTimersRef.current[tempId]
    if (t) {
      clearTimeout(t)
      delete debounceTimersRef.current[tempId]
    }
    setPhase(tempId, 'idle')
  }, [setPhase])

  useEffect(() => {
    return () => {
      Object.values(debounceTimersRef.current).forEach(clearTimeout)
      debounceTimersRef.current = {}
    }
  }, [])

  return {
    scheduleMetricBudgetSync,
    cancelMetricBudgetSync,
    syncPhaseByTempId,
    getMetricBudgetSyncPhase: (tempId: string): MetricBudgetSyncPhase =>
      syncPhaseByTempId[tempId] ?? 'idle'
  }
}
